
import { TAX_TYPE } from "data/taxes";
import { IState } from "redux/reducers/createInvoiceSlice/types";
import { formatToRupees } from "utils/formattedAmount";

export function getAmount(particular: any) {
  const { discount, discountType, rate, units } = particular;

  if (!rate) return 0;

  let result = units * rate;
  if (discount && discountType === "PERCENT") {
    result = result - (result * discount) / 100;
  }

  if (discount && discountType === "AMOUNT") {
    result = result - discount;
  }

  result = Number(result.toFixed(2))
  return result;
}

export function getTotalCharges(otherParticulars: any[]) {
  const total = otherParticulars.reduce(
    (acc, particular) => acc + Math.round(particular.amount * 100),
    0
  );
  return total / 100; // Convert back to the original scale
}

export function getSubTotal(particulars: any[]) {
  return particulars.reduce(
    (acc, particular) => acc + getAmount(particular),
    0
  );
}

export function getGstAmount(particular: any, value: string) {

  const taxableAmount = getAmount(particular);

  if (!value) return 0;

  let gstPercent = 0;

  switch (value) {
    case TAX_TYPE.NON_GST_SUPPLY:
      gstPercent = 0;
      break;
    case TAX_TYPE.NON_TAXABLE:
      gstPercent = 0;
      break;
    case TAX_TYPE.OUT_OF_SCOPE:
      gstPercent = 0;
      break;
    case TAX_TYPE.GST0:
      gstPercent = 0;
      break;
    case TAX_TYPE.GST0_1:
      gstPercent = 0.1;
      break;
    case TAX_TYPE.GST0_25:
      gstPercent = 0.25;
      break;
    case TAX_TYPE.GST1:
      gstPercent = 1;
      break;
    case TAX_TYPE.GST1_5:
      gstPercent = 1.5;
      break;
    case TAX_TYPE.GST3:
      gstPercent = 3;
      break;
    case TAX_TYPE.GST5:
      gstPercent = 5;
      break;
    case TAX_TYPE.GST6:
      gstPercent = 6;
      break;
    case TAX_TYPE.GST7_5:
      gstPercent = 7.5;
      break;
    case TAX_TYPE.GST12:
      gstPercent = 12;
      break;
    case TAX_TYPE.GST18:
      gstPercent = 18;
      break;
    case TAX_TYPE.GST28:
      gstPercent = 28;
      break;
    case TAX_TYPE.GST_40:
      gstPercent = 40;
      break;
    default:
      gstPercent = 0;
  }
  return (taxableAmount * gstPercent) / 100;
}

export function getTotalGst(particulars: any[]) {
  return particulars.reduce((acc, particular) => {
    return acc + getGstAmount(particular, particular.gst?.value);
  }, 0);
}

export function getTotalTds(particulars: any[], gstPercent: string | null) {
  const preciseRound = (value: number) => Math.round(value * 100) / 100;

  const taxableValue = particulars.reduce((acc, particular) => {
    const amount = getAmount(particular);
    return preciseRound(acc + amount);
  }, 0);

  const tds = (taxableValue * Number(gstPercent)) / 100;
  const roundedTds = preciseRound(tds);
  return roundedTds;
}
// GST TDS for a saved invoice/proforma, from the stored percentages (gstTdsIgst / gstTdsCgst / gstTdsSgst).
// If the percentages were not stored, fall back to IGST 2% or CGST 1% + SGST 1% based on isIgst.
export function getSavedGstTds(result: any, isIgst: boolean) {
  const preciseRound = (value: number) => Math.round(value * 100) / 100;
  const taxableValue = Number(result?.subTotal) || 0;
  const hasSavedRates = [result?.gstTdsIgst, result?.gstTdsCgst, result?.gstTdsSgst].some((rate) => Number(rate) > 0);

  const igstRate = hasSavedRates ? Number(result?.gstTdsIgst) || 0 : isIgst ? 2 : 0;
  const cgstRate = hasSavedRates ? Number(result?.gstTdsCgst) || 0 : isIgst ? 0 : 1;
  const sgstRate = hasSavedRates ? Number(result?.gstTdsSgst) || 0 : isIgst ? 0 : 1;

  const igst = preciseRound((taxableValue * igstRate) / 100);
  const cgst = preciseRound((taxableValue * cgstRate) / 100);
  const sgst = preciseRound((taxableValue * sgstRate) / 100);

  return {
    igstRate,
    cgstRate,
    sgstRate,
    igst,
    cgst,
    sgst,
    total: result?.hasGstTds ? igst + cgst + sgst : 0,
  };
}
// GST TDS (Sec 51): 2% of taxable value — IGST 2% for inter-state, CGST 1% + SGST 1% for intra-state
// locationOfSupply defaults to the proforma location; invoices pass state.locationOfSupply
export function getGstTds(state: any, locationOfSupply: any = state.proformaLocationOfSupply) {
  const preciseRound = (value: number) => Math.round(value * 100) / 100;
  const taxableValue = getSubTotal(state.particulars);
  const interState = locationOfSupply === state.placeOfSupply;
  const isIgst = !interState || state.divideTax;
  const cgst = preciseRound(taxableValue * 0.01);
  const sgst = preciseRound(taxableValue * 0.01);
  const igst = preciseRound(taxableValue * 0.02);
  const enabled = state.hasGst && state.hasGstTds;

  return {
    isIgst,
    cgst,
    sgst,
    igst,
    total: enabled ? (isIgst ? igst : cgst + sgst) : 0,
  };
}


export function getRoundOff(state: IState) {
  let totalAmount = getSubTotal(state.particulars);
  let totalGst = state.locationOfSupply !== state?.placeOfSupply ? getTotalGst(state.particulars) : ((formatToRupees(getTotalGst(state.particulars) / 2)) + formatToRupees(getTotalGst(state.particulars) / 2));
  let totalOtherParticularCharges = getTotalCharges(state.otherParticulars);
  let adjustment = +state.adjustment;
  let result =
    state?.hasGst ? (totalAmount + totalGst + totalOtherParticularCharges + adjustment) : (totalAmount + totalOtherParticularCharges + adjustment);

  let roundOff = Math.round(result) - result;

  return roundOff.toFixed(1);
}

export function getGrandTotal(state: IState) {
  const interState = state.locationOfSupply === state?.placeOfSupply;
  let totalAmount = getSubTotal(state.particulars);
  let totalGst = interState ? (parseFloat((getTotalGst(state.particulars) / 2).toFixed(2)) + parseFloat((getTotalGst(state.particulars) / 2).toFixed(2))) : getTotalGst(state.particulars).toFixed(2);
  let totalOtherParticularCharges = getTotalCharges(state.otherParticulars);
  let adjustment = +state.adjustment;
  let result = state.hasGst ?
    (totalAmount + parseFloat(totalGst) + totalOtherParticularCharges + adjustment) : (totalAmount + totalOtherParticularCharges + adjustment);
  // return (Math.round(+result));

  return +result.toFixed(2);
}


export function formatNumber(value: any) {

  if (typeof value !== 'number' || isNaN(value)) {
    return null;
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

//Gst Report

export function getTotalGstReport(particulars: any[]) {

  const value = particulars?.reduce((acc, particular) => {
    return acc + getGstAmount(particular, particular.gst);
  }, 0);
  return value;

}
